import { useIsMobile } from '@app/app/hooks/useIsMobile';
import { LinearProgress } from '@app/ui/progress';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SurveyItem } from '@app/application/components/item/SurveyItem';
import { Questionnaire } from '@app/questionnaire/types/Questionnaire';
import { ReactNode, useMemo } from 'react';
import { calculateSurveyProgress, isVisible } from '@app/dynamic/utils';
import { questionnaireConfig } from '@app/questionnaire/configs';
import { QuestionnaireStatusEnum } from '@app/questionnaire/constants';

import { paramCase } from 'change-case';
import { getProgressStatusConfig } from '@app/shared/utils';
import { FinishAndSubmit } from '@app/shared/components/buttons/FinishAndSubmit';

export interface QuestionnaireListProps {
  readonly item: Questionnaire;
  readonly isAdmin?: boolean;
  readonly onSubmit?: () => void;
  readonly onClick: (survey: string) => void;
  /** Admin view only — rendered right-aligned on the ring header's single row. */
  readonly actions?: ReactNode;
}

export const QuestionnaireList = ({ item, isAdmin, onClick, onSubmit, actions }: QuestionnaireListProps) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();

  const questionnaireProgress = useMemo(() => calculateSurveyProgress(questionnaireConfig.surveys, item), [item]);

  const surveysConfig = useMemo(() => questionnaireConfig.surveys.filter(survey => isVisible(survey, item)), [item]);

  // "N of M sections completed" uses the SAME predicate as each row's own chip
  // (populatedPageCount === pageCount), and M is the isVisible-filtered list — see ApplicationList.
  const completedSectionsCount = useMemo(
    () =>
      surveysConfig.filter(survey => {
        const progressConfig = questionnaireProgress.populatedSurveyCountMap[survey.name];

        return progressConfig?.populatedPageCount === progressConfig?.pageCount;
      }).length,
    [surveysConfig, questionnaireProgress],
  );

  const notFinished = questionnaireProgress.progressTotal !== 100;

  const isSubmittedQuestionnaire = item.status !== QuestionnaireStatusEnum.applicants && !item.isUnlocked && !isAdmin;

  return (
    <>
      {/* Shared component: the parent's own page passes no `isAdmin` and keeps the linear bar. */}
      {isAdmin ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {/* Two rings: determinate CircularProgress draws only the arc, so the track is a
            separate value={100} layer underneath. */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={72}
              thickness={4}
              sx={{ color: 'progressTrack.main' }}
            />
            <CircularProgress
              variant="determinate"
              value={questionnaireProgress.progressTotal}
              size={72}
              thickness={4}
              sx={{ position: 'absolute', left: 0, color: 'progressAccent.main' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'progressAccent.main' }}>
                {questionnaireProgress.progressTotal}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography component="h3" sx={{ fontSize: 24, fontWeight: 600, color: 'text.primary' }}>
              {t('user.page.singleParent.subtitle')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: '2px' }}>
              {t('user.page.single.sectionsCompleted', {
                done: completedSectionsCount,
                total: surveysConfig.length,
              })}
            </Typography>
          </Box>
          {actions && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>{actions}</Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: {
              sm: 'flex',
            },
            alignItems: 'center',
            mb: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant={isMobile ? 'h2' : 'h3'}
            component="h3"
            sx={{
              whiteSpace: 'nowrap',
              mr: {
                sm: 3,
                md: 5,
              },
              mb: {
                xs: 1,
                sm: 0,
              },
            }}
          >
            {t('user.page.single.currentProgress')}
          </Typography>
          <LinearProgress value={questionnaireProgress.progressTotal} />
        </Box>
      )}

      <Paper
        sx={{
          borderRadius: '20px',
        }}
        variant="outlined"
        elevation={0}
      >
        {surveysConfig.map(survey => {
          const progressConfig = questionnaireProgress.populatedSurveyCountMap[survey.name];

          const { status, icon, iconButton, color } = getProgressStatusConfig(
            progressConfig.populatedPageCount,
            progressConfig.pageCount,
          );

          const handleSurvey = () => onClick(paramCase(survey.name));

          return (
            <SurveyItem
              key={survey.name}
              surveyLabel={t(`questionnaire.field.survey.option.${survey.name}.name`, item)}
              progressConfig={progressConfig}
              statusLabel={t(`questionnaire.form.status.${status}`)}
              color={color}
              icon={icon}
              iconButton={iconButton}
              isAdmin={isAdmin || false}
              isSubmitted={isSubmittedQuestionnaire}
              isMobile={isMobile}
              onClick={handleSurvey}
            />
          );
        })}
      </Paper>

      {!isAdmin && (item.status === QuestionnaireStatusEnum.applicants || item.isUnlocked) && (
        <FinishAndSubmit
          onSubmit={onSubmit}
          notFinished={notFinished}
          tooltipText={t('application.button.finishAndSubmit.description')}
          buttonText={t('questionnaire.button.finishAndSubmit.name')}
        />
      )}
    </>
  );
};
